import React, { useMemo } from 'react';
import { 
  Calendar, CheckCircle2, FileText, 
  Shield, Home, Car, Briefcase, Phone, Plane, Megaphone, Banknote, Scale,
  Loader2, Sparkles, AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge'; 

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const TimelineWidget = ({ documents = [], isLoading = false }) => {
  const { language, t } = useLanguage(); // Dil fonksiyonunu al
  
  // Kategori ikonlarını belirleyen yardımcı fonksiyon (useMemo dışında kalabilir)
  const getCategoryIcon = (category) => {
    const cat = category ? category.toLowerCase() : '';
    const icons = {
      tax: FileText, insurance: Shield, housing: Home, car: Car,
      office: Briefcase, communication: Phone, travel: Plane,
      advertising: Megaphone, finance: Banknote, legal: Scale
    };
    return icons[cat] || FileText;
  };

  // ✅ KRİTİK DÜZELTME: useEffect ve useState yerine useMemo kullanıyoruz.
  // Bu sayede "Maximum update depth" hatası imkansız hale gelir.
  const deadlines = useMemo(() => {
    if (!documents || documents.length === 0) return [];

    return documents.map(doc => {
      const d = new Date(doc.date || doc.created_at);
      const displayDate = d.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'de-DE', { 
        day: '2-digit', 
        month: '2-digit' 
      });

      let type = 'safe';
      let statusText = displayDate;
      const isPaid = doc.statusKey === 'status_paid';

      if (isPaid) {
        type = 'safe';
        statusText = t('status_paid') || 'Paid';
      }

      return {
        id: doc.id || Math.random(),
        date: displayDate,
        title: doc.merchant_name || (t('untitled_doc') || 'Untitled Document'),
        statusText,
        type,
        icon: getCategoryIcon(doc.category),
        isPaid
      };
    }).slice(0, 10); // Sadece son 10 işlemi al
  }, [documents, language, t]); 

  const getStyle = (type, isPaid) => {
    if (isPaid) return { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: 'text-emerald-500', bar: 'bg-emerald-500' };
    if (type === 'urgent') return { border: 'border-rose-500/30', bg: 'bg-rose-500/10', text: 'text-rose-400', icon: 'text-rose-500', bar: 'bg-rose-500' };
    return { border: 'border-white/10', bg: 'bg-[#0f172a]', text: 'text-slate-400', icon: 'text-slate-500', bar: 'bg-slate-600' };
  };

  if (isLoading) return (
    <div className="w-full h-40 bg-[#0f172a] rounded-[2.5rem] animate-pulse border border-white/5 mb-8 flex items-center justify-center">
       <Loader2 size={24} className="animate-spin text-slate-500" />
    </div>
  );

  return (
    <section className="w-full relative font-sans mb-10">
      <div className="flex items-center justify-between mb-6 px-2">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-orange-600 rounded-xl shadow-[0_0_20px_rgba(234,88,12,0.3)]">
            <Calendar size={20} className="text-white" strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] italic flex items-center gap-2">
              {t('timeline_title') || 'TimeLine'}
            </h3>
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest leading-none mt-1">{t('recent_activity') || 'Recent Activity'}</p>
          </div>
        </div>
        {deadlines.length > 0 && (
            <Badge className="bg-white/5 text-slate-400 border border-white/10 text-[9px] font-black tracking-widest px-3 py-1 backdrop-blur-md">
                {deadlines.length} {t('total_documents') || 'Total'}
            </Badge>
        )}
      </div>

      {deadlines.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-10 rounded-[2.5rem] border border-dashed border-white/10 bg-[#0f172a]/50 text-center space-y-4">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <Sparkles size={28} className="text-slate-500" />
            </div>
            <div className="space-y-1">
                <p className="text-sm font-black text-white uppercase tracking-tight">{t('no_documents_yet') || 'No Documents Yet'}</p>
                <p className="text-[10px] text-slate-500 font-medium italic opacity-70">{t('system_clean') || 'System clean. Initiate FoxRadar.'}</p>
            </div>
        </motion.div>
      ) : (
        <div className="overflow-x-auto scrollbar-hide snap-x snap-mandatory -mx-4 px-4 pb-4">
          <div className="flex gap-4 min-w-max">
            {deadlines.map((item, idx) => {
              const ui = getStyle(item.type, item.isPaid);
              const Icon = item.icon;
              return (
                <motion.div 
                  key={item.id || idx}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className={`snap-center relative w-60 p-6 rounded-[2rem] bg-[#0f172a] border ${ui.border} shadow-xl transition-all cursor-pointer ring-1 ring-white/5 overflow-hidden group`}
                >
                  <div className={`absolute top-0 left-0 bottom-0 w-1.5 ${ui.bar} shadow-[0_0_10px] opacity-80`} />
                  <div className={`absolute inset-0 ${ui.bg} opacity-20 group-hover:opacity-30 transition-opacity`} />

                  <div className="flex items-start justify-between mb-5 relative z-10">
                    <div className={`p-3 rounded-2xl bg-white/5 border border-white/5 ${ui.icon} shadow-inner`}>
                      <Icon size={24} strokeWidth={2} />
                    </div>
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest bg-black/40 px-2 py-1 rounded-md border border-white/5">{item.date}</span>
                  </div>

                  <h4 className="text-sm font-black text-white uppercase tracking-tight leading-snug line-clamp-2 h-10 mb-4 italic relative z-10 group-hover:text-orange-500 transition-colors">
                    {item.title}
                  </h4>

                  <div className={`flex items-center gap-2 ${ui.text} relative z-10`}>
                    {item.type === 'urgent' && !item.isPaid ? <AlertTriangle size={14} className="animate-pulse" /> : <CheckCircle2 size={14} />}
                    <span className="text-[9px] font-black uppercase tracking-widest">{item.statusText}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default TimelineWidget;